import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MySchoolProfilePage() {
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [udiseCode, setUdiseCode] = useState(searchParams.get('udise') || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Removal request state
  const [showRemoveModal, setShowRemoveModal] = useState(searchParams.get('action') === 'remove');
  const [removeForm, setRemoveForm] = useState({ requester_name: '', requester_role: 'Principal', requester_phone: '', reason: '' });
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [removeDone, setRemoveDone] = useState(null);

  const submitRemoval = async () => {
    if (!removeForm.requester_name || !removeForm.requester_phone) {
      setError(language === 'hi' ? 'नाम और phone ज़रूरी है' : 'Name and phone required');
      return;
    }
    if (!/^\d{10,13}$/.test(removeForm.requester_phone.replace(/\D/g, ''))) {
      setError(language === 'hi' ? 'वैध phone number डालें' : 'Enter a valid phone number');
      return;
    }
    setRemoveSubmitting(true);
    setError('');
    try {
      const payload = {
        udise_code: result?.udise_code || udiseCode || null,
        school_name: result?.preview?.school_name || null,
        requester_name: removeForm.requester_name.trim(),
        requester_role: removeForm.requester_role,
        requester_phone: removeForm.requester_phone.trim(),
        reason: removeForm.reason.trim(),
        confirm: true,
      };
      const r = await fetch(`${API_URL}/api/udise/remove-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (r.ok && data.success) {
        setRemoveDone(data);
      } else {
        setError(data.detail || (language === 'hi' ? 'Request submit करने में problem हुई' : 'Error submitting request'));
      }
    } catch (e) {
      setError(language === 'hi' ? 'Network error' : 'Network error');
    } finally {
      setRemoveSubmitting(false);
    }
  };

  const lookup = async (code) => {
    const c = (code || udiseCode).trim();
    if (!c) {
      setError(language === 'hi' ? 'कृपया UDISE code डालें' : 'Please enter UDISE code');
      return;
    }
    if (!/^\d{11}$/.test(c)) {
      setError(language === 'hi' ? 'UDISE code 11 digits का होता है' : 'UDISE code must be 11 digits');
      return;
    }
    setError('');
    setLoading(true);
    setResult(null);
    try {
      const r = await fetch(`${API_URL}/api/udise/lookup-public/${c}`);
      const data = await r.json();
      setResult(data);
      setSearchParams({ udise: c });
    } catch (e) {
      setError(language === 'hi' ? 'Network error — दोबारा try करें' : 'Network error — please try again');
    } finally {
      setLoading(false);
    }
  };

  // Auto-lookup if UDISE code in URL
  useEffect(() => {
    if (searchParams.get('udise') && !result) {
      lookup(searchParams.get('udise'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-green-50" data-testid="my-school-profile-page">
      {/* Top Nav */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-home">
            <img src="https://customer-assets.emergentagent.com/job_b71b88a0-de31-4e82-b047-c99df2477168/artifacts/7dlw52ts_logo%20%281%29.jpg" alt="Logo" className="h-10 w-10 rounded-lg" />
            <span className="font-bold text-lg bg-gradient-to-r from-orange-500 to-green-600 bg-clip-text text-transparent" style={{fontFamily: 'Outfit, sans-serif'}}>शिक्षा मित्र</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-slate-600 hover:text-orange-600 transition" data-testid="nav-back-home">
              ← {language === 'hi' ? 'Home' : 'Home'}
            </Link>
            <Link to="/login" className="px-4 py-2 bg-orange-500 text-white rounded-full text-sm font-semibold hover:bg-orange-600" data-testid="nav-login">
              {language === 'hi' ? 'Login' : 'Login'}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-4 md:px-8 pt-12 pb-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-bold uppercase tracking-wider mb-4" data-testid="badge-schools-count">
            🎓 3.9 लाख+ Schools हमारे पास
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight mb-5" style={{fontFamily: 'Outfit, sans-serif'}}>
            {language === 'hi' 
              ? <>क्या आपकी School <span className="bg-gradient-to-r from-orange-500 to-green-600 bg-clip-text text-transparent">हमारे Database</span> में है?</>
              : <>Is Your School <span className="bg-gradient-to-r from-orange-500 to-green-600 bg-clip-text text-transparent">In Our Database</span>?</>}
          </h1>
          <p className="text-slate-600 text-base md:text-lg mb-8 max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
            {language === 'hi' 
              ? 'अपना UDISE code डालिए — हम बताएँगे कि आपकी school की कितनी जानकारी हमारे पास पहले से मौजूद है। Onboarding में आपको बस 1 field भरना होगा!'
              : 'Enter your UDISE code — we will show you how much of your school info we already have. Onboarding needs just 1 field from you!'}
          </p>

          {/* Search Box */}
          <div className="max-w-xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3 bg-white rounded-2xl shadow-xl border-2 border-orange-200 p-3">
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                pattern="\d{11}"
                maxLength={11}
                value={udiseCode}
                onChange={(e) => { setUdiseCode(e.target.value.replace(/\D/g, '').slice(0, 11)); setError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
                placeholder={language === 'hi' ? '11-digit UDISE code...' : '11-digit UDISE code...'}
                className="flex-1 px-5 py-4 text-lg font-mono tracking-wider bg-slate-50 rounded-xl focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-400"
                data-testid="udise-input"
              />
              <button
                onClick={() => lookup()}
                disabled={loading || udiseCode.length !== 11}
                className="px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg whitespace-nowrap"
                data-testid="lookup-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    {language === 'hi' ? 'Search...' : 'Search...'}
                  </span>
                ) : (
                  <>🔍 {language === 'hi' ? 'Search करें' : 'Search'}</>
                )}
              </button>
            </div>
            {error && <p className="text-red-600 text-sm mt-2" data-testid="error-msg">{error}</p>}
            <p className="text-slate-400 text-xs mt-3">
              {language === 'hi' ? 'UDISE code कहाँ मिलेगा? ' : "Don't know your UDISE code? "}
              <a href="https://udiseplus.gov.in" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                udiseplus.gov.in →
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Result Section */}
      {result && result.success && result.found && (
        <section className="px-4 md:px-8 pb-16">
          <div className="max-w-4xl mx-auto">
            {/* Success Card */}
            <div className="bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 text-white rounded-3xl p-8 md:p-10 shadow-2xl mb-8 relative overflow-hidden" data-testid="school-found-card">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-yellow-300/20 rounded-full blur-3xl"></div>
              <div className="relative">
                <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur rounded-full text-xs font-bold mb-3 uppercase tracking-wider">
                  ✅ {language === 'hi' ? 'School मिली!' : 'School Found!'}
                </div>
                <h2 className="text-3xl md:text-4xl font-black mb-2" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="found-school-name">
                  {result.preview.school_name}
                </h2>
                <p className="text-white/90 text-lg mb-4">
                  📍 {result.preview.district}, {result.preview.state}
                </p>
                <div className="flex flex-wrap gap-2">
                  {result.preview.board && result.preview.board !== 'Unknown' && (
                    <span className="px-3 py-1.5 bg-white/20 backdrop-blur rounded-full text-sm font-semibold">
                      🎓 {result.preview.board}
                    </span>
                  )}
                  {result.preview.school_category && (
                    <span className="px-3 py-1.5 bg-white/20 backdrop-blur rounded-full text-sm font-semibold">
                      📚 {result.preview.school_category}
                    </span>
                  )}
                  {result.preview.established_year && (
                    <span className="px-3 py-1.5 bg-white/20 backdrop-blur rounded-full text-sm font-semibold">
                      📅 {language === 'hi' ? 'स्थापना' : 'Est.'} {result.preview.established_year}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Stats Preview Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="preview-stats">
              <StatCard icon="👨‍🎓" label={language === 'hi' ? 'Students' : 'Students'} value={result.preview.total_students || '—'} color="blue" />
              <StatCard icon="👨‍🏫" label={language === 'hi' ? 'Teachers' : 'Teachers'} value={result.preview.total_teachers || '—'} color="green" />
              <StatCard icon="📚" label={language === 'hi' ? 'Board' : 'Board'} value={result.preview.board === 'Unknown' ? (language === 'hi' ? 'पता नहीं' : 'Unknown') : result.preview.board} color="orange" />
              <StatCard icon="🏫" label={language === 'hi' ? 'UDISE Code' : 'UDISE Code'} value={result.udise_code} color="purple" isCode />
            </div>

            {/* Data Coverage Teaser */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 md:p-8 mb-8" data-testid="coverage-teaser">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h3 className="text-xl md:text-2xl font-black text-slate-900" style={{fontFamily: 'Outfit, sans-serif'}}>
                  📊 {language === 'hi' ? 'आपकी School के बारे में हमारे पास' : 'What We Have About Your School'}
                </h3>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-bold">
                  76 Fields · 94% Core Profile
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { hi: 'Basic Details (नाम, प्रकार, Category)', en: 'Basic Details (Name, Type, Category)', status: 'full' },
                  { hi: 'Location (पूरा पता, GPS coordinates)', en: 'Location (Full address, GPS)', status: 'full' },
                  { hi: 'Contact (Phone, Email, Website, Principal)', en: 'Contact (Phone, Email, Website, Principal)', status: 'full' },
                  { hi: 'Academic (Class range, Medium, Days)', en: 'Academic (Class range, Medium, Days)', status: 'full' },
                  { hi: 'Affiliation (CBSE/ICSE recognition)', en: 'Affiliation (CBSE/ICSE recognition)', status: 'full' },
                  { hi: 'Students & Teachers Count', en: 'Students & Teachers Count', status: 'full' },
                  { hi: 'Facilities (Anganwadi, Minority, CWSN)', en: 'Facilities (Anganwadi, Minority, CWSN)', status: 'partial' },
                  { hi: 'Infrastructure (Rooms, Toilets, Water)', en: 'Infrastructure (Rooms, Toilets, Water)', status: 'missing' },
                ].map((item, idx) => (
                  <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${
                    item.status === 'full' ? 'bg-green-50' :
                    item.status === 'partial' ? 'bg-yellow-50' : 'bg-slate-100'
                  }`}>
                    <span className="text-xl flex-shrink-0">
                      {item.status === 'full' ? '✅' : item.status === 'partial' ? '🟡' : '⚪'}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800">
                        {language === 'hi' ? item.hi : item.en}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {item.status === 'full' ? (language === 'hi' ? 'पूरी जानकारी उपलब्ध' : 'Full data available') :
                         item.status === 'partial' ? (language === 'hi' ? 'कुछ जानकारी उपलब्ध' : 'Partial data') :
                         (language === 'hi' ? 'आप खुद भरेंगे' : "You'll fill this yourself")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA — Sign Up to unlock */}
            <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-600 rounded-3xl p-8 md:p-10 text-white text-center shadow-2xl" data-testid="cta-signup-card">
              <h3 className="text-2xl md:text-4xl font-black mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>
                🚀 {language === 'hi' ? 'Free Demo Book करें' : 'Book Your Free Demo'}
              </h3>
              <p className="text-white/95 text-base md:text-lg max-w-2xl mx-auto mb-6">
                {language === 'hi' 
                  ? 'Sign up करते ही आपकी पूरी school profile auto-fill हो जाएगी। बस 3 मिनट में onboarding complete!'
                  : 'Sign up and your complete school profile gets auto-filled. Onboarding in just 3 minutes!'}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 max-w-2xl mx-auto">
                <div className="bg-white/15 backdrop-blur rounded-xl p-3 text-sm">
                  ✓ {language === 'hi' ? '50+ fields auto-fill' : '50+ fields auto-fill'}
                </div>
                <div className="bg-white/15 backdrop-blur rounded-xl p-3 text-sm">
                  ✓ {language === 'hi' ? 'Software Lifetime FREE' : 'Software Lifetime FREE'}
                </div>
                <div className="bg-white/15 backdrop-blur rounded-xl p-3 text-sm">
                  ✓ {language === 'hi' ? 'BYOK + BYODB — आपका data, आपका control' : 'BYOK + BYODB — your data, your control'}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate('/?demo=true&udise=' + result.udise_code)}
                  className="px-8 py-4 bg-white text-slate-900 rounded-full font-bold hover:bg-slate-100 transition shadow-xl"
                  data-testid="cta-book-demo"
                >
                  📅 {language === 'hi' ? 'Free Demo Book करें' : 'Book Free Demo'}
                </button>
                <a
                  href={`https://wa.me/919183100206?text=${encodeURIComponent(`नमस्ते, मेरी school "${result.preview.school_name}" (UDISE: ${result.udise_code}) के लिए Demo चाहिए।`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-green-600 text-white rounded-full font-bold hover:bg-green-700 transition shadow-xl flex items-center justify-center gap-2"
                  data-testid="cta-whatsapp"
                >
                  <i className="fa-brands fa-whatsapp text-xl"></i>
                  WhatsApp
                </a>
              </div>
            </div>

            {/* Try Another */}
            <button
              onClick={() => { setUdiseCode(''); setResult(null); setSearchParams({}); inputRef.current?.focus(); }}
              className="mt-6 w-full text-center text-slate-500 hover:text-orange-600 text-sm transition"
              data-testid="search-another-btn"
            >
              🔄 {language === 'hi' ? 'दूसरी school search करें' : 'Search another school'}
            </button>

            {/* Privacy Note + Remove Option */}
            <div className="mt-8 bg-slate-50 border border-slate-200 rounded-2xl p-5 text-sm" data-testid="privacy-note-block">
              <p className="text-slate-700 mb-3">
                🛡️ <strong>{language === 'hi' ? 'पारदर्शिता:' : 'Transparency:'}</strong>{' '}
                {language === 'hi'
                  ? 'यह जानकारी सरकारी UDISE+ portal और public records से है।'
                  : 'This information is from the government UDISE+ portal and public records.'}{' '}
                <Link to="/data-sources" className="text-orange-600 underline hover:text-orange-700" data-testid="link-data-sources">
                  {language === 'hi' ? 'विस्तार से पढ़ें' : 'Read full sources'} →
                </Link>
              </p>
              <button
                onClick={() => setShowRemoveModal(true)}
                className="text-sm px-4 py-2 bg-white border border-red-200 text-red-600 rounded-full font-medium hover:bg-red-50 transition"
                data-testid="btn-open-remove-modal"
              >
                🗑️ {language === 'hi' ? 'मेरी School को listing से हटाएँ' : 'Remove My School From Listing'}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Not Found */}
      {result && (!result.success || !result.found) && (
        <section className="px-4 md:px-8 pb-16">
          <div className="max-w-2xl mx-auto bg-amber-50 border-2 border-amber-200 rounded-2xl p-8 text-center" data-testid="school-not-found-card">
            <div className="text-5xl mb-3">🤔</div>
            <h3 className="text-xl md:text-2xl font-bold text-amber-900 mb-2">
              {language === 'hi' ? 'इस UDISE code की जानकारी नहीं मिली' : 'UDISE code not found in our database'}
            </h3>
            <p className="text-amber-800 mb-5">
              {language === 'hi' 
                ? 'कोई बात नहीं! Sign up करें और अपनी school की details manually भरें — हम step-by-step guide करेंगे।'
                : "No worries! Sign up and fill your school details manually — we'll guide you step by step."}
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={() => navigate('/?demo=true')}
                className="px-6 py-3 bg-orange-500 text-white rounded-full font-semibold hover:bg-orange-600"
                data-testid="not-found-cta-demo"
              >
                {language === 'hi' ? 'फिर भी Demo लें' : 'Still Book Demo'}
              </button>
              <button
                onClick={() => { setUdiseCode(''); setResult(null); setSearchParams({}); }}
                className="px-6 py-3 bg-white border border-slate-300 text-slate-700 rounded-full font-semibold hover:bg-slate-50"
                data-testid="not-found-try-again"
              >
                {language === 'hi' ? 'दूसरा code try करें' : 'Try another code'}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Below-fold: Trust indicators */}
      {!result && (
        <section className="px-4 md:px-8 py-12 bg-white/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-black text-center text-slate-900 mb-10" style={{fontFamily: 'Outfit, sans-serif'}}>
              {language === 'hi' ? '3.9 लाख+ Schools का Data — आपकी भी है?' : '3.9 Lakh+ Schools Database — Is Yours There?'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: '🇮🇳', hi: 'सभी States', en: 'All States', num: '36' },
                { icon: '🏫', hi: 'Schools', en: 'Schools', num: '4.48L' },
                { icon: '🎓', hi: 'UDISE Data', en: 'UDISE Data', num: '3.9L' },
                { icon: '📊', hi: 'Fields per School', en: 'Fields per School', num: '76' },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-sm text-center border border-slate-200 hover:border-orange-300 transition">
                  <div className="text-3xl mb-2">{s.icon}</div>
                  <div className="text-3xl font-black text-orange-600">{s.num}</div>
                  <div className="text-xs text-slate-500 mt-1">{language === 'hi' ? s.hi : s.en}</div>
                </div>
              ))}
            </div>
            <div className="text-center mt-6 text-sm text-slate-500">
              <Link to="/data-sources" className="underline hover:text-orange-600" data-testid="link-data-sources-bottom">
                🛡️ {language === 'hi' ? 'यह data कहाँ से आया? — पारदर्शिता देखें' : 'Where does this data come from? — See transparency'} →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Remove School Modal */}
      {showRemoveModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="remove-modal">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {removeDone ? (
              <div className="p-8 text-center">
                <div className="text-6xl mb-3">✅</div>
                <h3 className="text-2xl font-black text-slate-900 mb-3">
                  {language === 'hi' ? 'Request submit हो गई!' : 'Request Submitted!'}
                </h3>
                <p className="text-slate-600 mb-4">{language === 'hi' ? removeDone.message : (removeDone.message_en || removeDone.message)}</p>
                <p className="text-xs text-slate-400 mb-6">Request ID: <span className="font-mono">{removeDone.request_id}</span></p>
                <button
                  onClick={() => { setShowRemoveModal(false); setRemoveDone(null); setRemoveForm({ requester_name: '', requester_role: 'Principal', requester_phone: '', reason: '' }); }}
                  className="px-6 py-2.5 bg-orange-500 text-white rounded-full font-semibold hover:bg-orange-600"
                  data-testid="btn-close-remove-success"
                >
                  {language === 'hi' ? 'बंद करें' : 'Close'}
                </button>
              </div>
            ) : (
              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl md:text-2xl font-black text-slate-900" style={{fontFamily: 'Outfit, sans-serif'}}>
                      🗑️ {language === 'hi' ? 'School Listing से हटाएँ' : 'Remove School Listing'}
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      {language === 'hi' ? 'Verification के लिए कुछ details चाहिए।' : 'We need a few details for verification.'}
                    </p>
                  </div>
                  <button onClick={() => setShowRemoveModal(false)} className="text-2xl text-slate-400 hover:text-slate-700 leading-none" data-testid="btn-close-remove-modal">×</button>
                </div>

                {result?.preview?.school_name && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4 text-sm">
                    <p className="text-orange-900"><strong>{language === 'hi' ? 'School:' : 'School:'}</strong> {result.preview.school_name}</p>
                    {result.udise_code && <p className="text-orange-700 text-xs mt-0.5">UDISE: {result.udise_code}</p>}
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {language === 'hi' ? 'आपका नाम *' : 'Your Name *'}
                    </label>
                    <input
                      type="text"
                      value={removeForm.requester_name}
                      onChange={e => setRemoveForm({ ...removeForm, requester_name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:border-orange-400 focus:outline-none"
                      placeholder={language === 'hi' ? 'आपका पूरा नाम' : 'Your full name'}
                      data-testid="input-requester-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {language === 'hi' ? 'पद / Role *' : 'Role *'}
                    </label>
                    <select
                      value={removeForm.requester_role}
                      onChange={e => setRemoveForm({ ...removeForm, requester_role: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:border-orange-400 focus:outline-none"
                      data-testid="input-requester-role"
                    >
                      <option>Principal</option>
                      <option>Owner</option>
                      <option>Director</option>
                      <option>School Admin</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {language === 'hi' ? 'Phone Number *' : 'Phone Number *'}
                    </label>
                    <input
                      type="tel"
                      value={removeForm.requester_phone}
                      onChange={e => setRemoveForm({ ...removeForm, requester_phone: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:border-orange-400 focus:outline-none"
                      placeholder="9876543210"
                      data-testid="input-requester-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {language === 'hi' ? 'कारण (Optional)' : 'Reason (Optional)'}
                    </label>
                    <textarea
                      value={removeForm.reason}
                      onChange={e => setRemoveForm({ ...removeForm, reason: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:border-orange-400 focus:outline-none resize-none"
                      rows={2}
                      placeholder={language === 'hi' ? 'क्यों हटाना है?' : 'Why remove?'}
                      data-testid="input-reason"
                    />
                  </div>
                </div>

                {error && <p className="text-red-600 text-sm mt-3" data-testid="remove-error">{error}</p>}

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mt-4 text-xs text-slate-600">
                  {language === 'hi'
                    ? '24-48 घंटे में हमारी team verify करके आपकी school को public listing से remove कर देगी। आपको WhatsApp पर confirmation मिलेगा।'
                    : 'Our team will verify and remove your school from the public listing within 24-48 hours. You will receive WhatsApp confirmation.'}
                </div>

                <div className="flex gap-2 mt-5">
                  <button
                    onClick={submitRemoval}
                    disabled={removeSubmitting}
                    className="flex-1 px-6 py-3 bg-red-600 text-white rounded-full font-bold hover:bg-red-700 disabled:opacity-50"
                    data-testid="btn-submit-removal"
                  >
                    {removeSubmitting ? (language === 'hi' ? 'Submit...' : 'Submitting...') : (language === 'hi' ? 'Request Submit करें' : 'Submit Request')}
                  </button>
                  <button
                    onClick={() => setShowRemoveModal(false)}
                    className="px-6 py-3 bg-slate-200 text-slate-700 rounded-full font-medium hover:bg-slate-300"
                    data-testid="btn-cancel-remove"
                  >
                    {language === 'hi' ? 'Cancel' : 'Cancel'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Small helper component
function StatCard({ icon, label, value, color, isCode }) {
  const bg = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
  }[color];
  return (
    <div className={`${bg} rounded-2xl p-5 border-2 text-center`}>
      <div className="text-3xl mb-1">{icon}</div>
      <div className={`${isCode ? 'text-sm font-mono' : 'text-2xl font-black'}`} style={{fontFamily: isCode ? 'monospace' : 'Outfit, sans-serif'}}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}
